import React from 'react';
import { useDocumentMeta } from '../hooks/useDocumentMeta';
import { PageHero } from '../components/PageHero';
import { Award, Map, Eye, Phone, MapPin, HelpCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import { PlanYourVisitCTA } from '../components/PlanYourVisitCTA';
import { BrandLogoCard } from '../components/BrandLogoCard';
import { brandGroups } from '../constants/brands';
import { PHONE, PHONE_TEL, GOOGLE_MAPS_URL } from '../constants/business';
export function Brands() {
  useDocumentMeta('Brands We Work With | Kjergaard Sports', 'Major firearms, optics, and ammunition brands at Kjergaard Sports — Benelli, Browning, Beretta, Tikka, Sako, Pulsar, Vortex, Federal, Winchester, and more.');
  const sectionBackgrounds = ['bg-brand-bone', 'bg-brand-offwhite', 'bg-brand-cream'];
  return <div data-id="element-279">
      <PageHero title="Brands We Work With" image="https://cdn.magicpatterns.com/uploads/h2HHsk76CJxRY2pmsyL8mj/firearms-wall.jpg" alt="Major brand firearms wall at Kjergaard Sports" data-id="element-280" />

      {/* Intro + dealer highlights */}
      <section className="py-20 bg-brand-offwhite" data-id="element-281">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-282">
          <div className="max-w-3xl mb-12" data-id="element-283">
            <h2 className="text-3xl md:text-4xl font-serif text-brand-charcoal mb-6" data-id="element-284">
              Major Brands, Real In-Store Knowledge
            </h2>
            <p className="text-lg text-brand-charcoal/80 leading-relaxed" data-id="element-285">
              Kjergaard Sports works closely with many of the names hunters,
              shooters, and collectors already trust. Visit the store to compare
              options in person, ask questions, and get help finding the right
              fit.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 mb-4" data-id="element-286">
            <div className="bg-brand-cream border border-brand-bone rounded-sm p-6 text-center" data-id="element-287">
              <Award className="w-10 h-10 text-brand-leather mx-auto mb-4" data-id="element-288" />
              <h3 className="font-serif text-xl text-brand-charcoal mb-2" data-id="element-289">
                Sako Select Dealer
              </h3>
              <p className="text-brand-charcoal/75 text-sm" data-id="element-290">
                Kjergaard Sports is a Sako Select dealer.
              </p>
            </div>
            <div className="bg-brand-cream border border-brand-bone rounded-sm p-6 text-center" data-id="element-291">
              <Eye className="w-10 h-10 text-brand-leather mx-auto mb-4" data-id="element-292" />
              <h3 className="font-serif text-xl text-brand-charcoal mb-2" data-id="element-293">
                Pulsar Top-3 Dealer
              </h3>
              <p className="text-brand-charcoal/75 text-sm" data-id="element-294">
                Kjergaard Sports was Pulsar's #3 store in the country last year.
              </p>
            </div>
            <div className="bg-brand-cream border border-brand-bone rounded-sm p-6 text-center" data-id="element-295">
              <Map className="w-10 h-10 text-brand-leather mx-auto mb-4" data-id="element-296" />
              <h3 className="font-serif text-xl text-brand-charcoal mb-2" data-id="element-297">
                Borders Three States
              </h3>
              <p className="text-brand-charcoal/75 text-sm" data-id="element-298">
                Serving Minnesota, South Dakota, and Iowa customers regularly.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Brand groups */}
      {brandGroups.map((group, idx) => <section key={group.heading} className={`py-20 ${sectionBackgrounds[idx % sectionBackgrounds.length]}`} data-id="element-299">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-300">
            <div className="max-w-3xl mb-10" data-id="element-301">
              <h2 className="text-3xl md:text-4xl font-serif text-brand-charcoal mb-4" data-id="element-302">
                {group.heading}
              </h2>
              <div className="h-1 w-20 bg-brand-leather mb-6" data-id="element-303"></div>
              <p className="text-brand-charcoal/75 leading-relaxed" data-id="element-304">
                {group.description}
              </p>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4" data-id="element-305">
              {group.brands.map(brand => <BrandLogoCard key={brand.name} brand={brand} data-id="element-306" />)}
            </div>

            {group.heading === 'Firearms' && <p className="text-sm text-brand-charcoal/70 italic mt-8 leading-relaxed max-w-3xl" data-id="element-307">
                Benelli, Franchi, Stoeger, and Uberti are related brands under
                the same parent company. Beretta, Tikka, and Sako are also
                related brands.
              </p>}
            {group.heading === 'Ammunition' && <p className="text-sm text-brand-charcoal/70 italic mt-8 leading-relaxed max-w-3xl" data-id="element-308">
                Winchester appears as both firearms and ammunition.
              </p>}
          </div>
        </section>)}

      {/* CTAs */}
      <section className="py-20 bg-brand-hunter text-brand-offwhite" data-id="element-309">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-310">
          <h2 className="text-3xl md:text-4xl font-serif mb-6" data-id="element-311">
            Not Sure Where to Start?
          </h2>
          <p className="text-lg text-brand-offwhite/85 mb-10 leading-relaxed" data-id="element-312">
            Stop in and we'll help you compare brands and models side by side.
            Call ahead with specific questions and we'll have things ready for
            you.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4" data-id="element-313">
            <a href={PHONE_TEL} className="bg-brand-leather hover:bg-brand-wood text-brand-offwhite px-7 py-3.5 rounded-sm font-medium transition-colors inline-flex items-center justify-center" data-id="element-314">
              <Phone className="w-5 h-5 mr-2" data-id="element-315" />
              Call {PHONE}
            </a>
            <a href={GOOGLE_MAPS_URL} target="_blank" rel="noopener noreferrer" className="border border-brand-offwhite/40 hover:border-brand-offwhite text-brand-offwhite px-7 py-3.5 rounded-sm font-medium transition-colors inline-flex items-center justify-center" data-id="element-316">
              <MapPin className="w-5 h-5 mr-2" data-id="element-317" />
              Visit the Store
            </a>
            <Link to="/contact" className="border border-brand-offwhite/40 hover:border-brand-offwhite text-brand-offwhite px-7 py-3.5 rounded-sm font-medium transition-colors inline-flex items-center justify-center" data-id="element-318">
              <HelpCircle className="w-5 h-5 mr-2" data-id="element-319" />
              Ask About Brands We Carry
            </Link>
          </div>
        </div>
      </section>

      <PlanYourVisitCTA data-id="element-320" />
    </div>;
}